import type { ProductPackage } from '@/types/auth';
import {
  allRequiredDone,
  JOB_COPY,
  visibleJobs,
  type HubJob,
} from '../jobs';
import {
  Eyebrow,
  Heading,
  Lede,
  PrimaryButton,
  StatusRow,
  StepBody,
  StepFooter,
} from '../OnboardingShell';

export function Hub({
  package: pkg,
  completed,
  businessName,
  welcomeBack,
  lockedJobs,
  onOpenJob,
  onGoLive,
}: {
  package: ProductPackage | null;
  completed: HubJob[];
  businessName: string;
  welcomeBack: boolean;
  /**
   * Jobs shown dimmed and non-interactive. Defaults to the product rule:
   * foundation / crawl / ads stay locked until prove is done; setup and
   * invite are always available. Pass explicitly to override.
   */
  lockedJobs?: HubJob[];
  onOpenJob: (job: HubJob) => void;
  onGoLive: () => void;
}) {
  const jobs = visibleJobs(pkg);
  const ready = allRequiredDone(pkg, completed);
  const locked =
    lockedJobs ??
    (completed.includes('prove')
      ? []
      : jobs.filter((job) => job === 'foundation' || job === 'crawl' || job === 'ads'));
  const title = businessName.trim()
    ? `Set up ${businessName.trim()}`
    : 'Set up your business';

  return (
    <>
      <StepBody>
        <Eyebrow>{welcomeBack ? 'Welcome back' : 'Your list'}</Eyebrow>
        <Heading>{title}</Heading>
        <Lede>
          {ready
            ? 'Everything required is done. Continue when you are ready.'
            : 'Tap a job to finish it. Come back here any time — this list is home.'}
        </Lede>
        {jobs.map((job) => {
          const done = completed.includes(job);
          const copy = JOB_COPY[job];
          return (
            <StatusRow
              key={job}
              tone={done ? 'done' : 'pending'}
              title={copy.title}
              detail={done ? copy.done : copy.pending}
              locked={locked.includes(job)}
              onClick={() => onOpenJob(job)}
            />
          );
        })}
      </StepBody>
      {ready && (
        <StepFooter>
          <PrimaryButton onClick={onGoLive}>Continue to dashboard</PrimaryButton>
        </StepFooter>
      )}
    </>
  );
}
